/**
 * Image compression utility for browser/WebView.
 * - Accepts data URL (base64) or Blob, resizes to max dimension and compresses to JPEG.
 * - Iteratively lowers quality until it meets targetBytes or minQuality.
 */
export type CompressOptions = {
  maxDimension?: number; // max width or height in px
  mime?: string; // output mime, default 'image/jpeg'
  initialQuality?: number; // 0..1
  minQuality?: number; // 0..1
  qualityStep?: number; // decrement step
  targetBytes?: number; // preferred target size
};

const DEFAULTS: Required<CompressOptions> = {
  maxDimension: 2000,
  mime: 'image/jpeg',
  initialQuality: 0.85,
  minQuality: 0.5,
  qualityStep: 0.1,
  targetBytes: 2.5 * 1024 * 1024,
};

export const dataUrlToBlob = (
  dataUrl: string,
): { mime: string; blob: Blob } => {
  const m = dataUrl.match(/^data:(.*?);base64,(.*)$/);
  if (!m) throw new Error('Invalid data URL');
  const mime = m[1] || 'application/octet-stream';
  const b64 = m[2];
  // Use atob if available (browser), fallback to Buffer for RN WebView polyfills
  const hasAtob =
    typeof globalThis !== 'undefined' &&
    typeof (globalThis as any).atob === 'function';
  const binary = hasAtob
    ? (globalThis as any).atob(b64)
    : Buffer.from(b64, 'base64').toString('binary');
  const len = binary.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) bytes[i] = binary.charCodeAt(i);
  const blob = new Blob([bytes], { type: mime });
  return { mime, blob };
};

const loadImage = async (
  blob: Blob,
): Promise<{
  bitmap?: ImageBitmap;
  img?: HTMLImageElement;
  width: number;
  height: number;
}> => {
  // Prefer createImageBitmap when available
  const canBitmap = typeof createImageBitmap === 'function';
  if (canBitmap) {
    const bitmap = await createImageBitmap(blob);
    return { bitmap, width: bitmap.width, height: bitmap.height };
  }
  // Fallback to HTMLImageElement
  const url = URL.createObjectURL(blob);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = (e) => reject(e);
      i.src = url;
    });
    return { img, width: img.width, height: img.height };
  } finally {
    URL.revokeObjectURL(url);
  }
};

const drawToCanvas = (
  source: { bitmap?: ImageBitmap; img?: HTMLImageElement },
  width: number,
  height: number,
): HTMLCanvasElement => {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D context not available');
  if (source.bitmap) {
    ctx.drawImage(source.bitmap, 0, 0, width, height);
  } else if (source.img) {
    ctx.drawImage(source.img, 0, 0, width, height);
  } else {
    throw new Error('No image source to draw');
  }
  return canvas;
};

const canvasToBlob = (
  canvas: HTMLCanvasElement,
  mime: string,
  quality: number,
): Promise<Blob> =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => {
        if (b) resolve(b);
        else reject(new Error('Failed to create blob'));
      },
      mime,
      quality,
    );
  });

export const compressImageToJpeg = async (
  input: string | Blob,
  opts?: CompressOptions,
): Promise<{ blob: Blob; mime: string; fileName: string }> => {
  const options = { ...DEFAULTS, ...(opts || {}) };

  // Normalize input to Blob
  const originalBlob =
    typeof input === 'string' ? dataUrlToBlob(input).blob : input;

  // Load image
  const loaded = await loadImage(originalBlob);
  const { width, height } = loaded;

  // Compute target dimensions
  const scale = Math.min(1, options.maxDimension / Math.max(width, height));
  const targetW = Math.max(1, Math.round(width * scale));
  const targetH = Math.max(1, Math.round(height * scale));

  // Draw
  const canvas = drawToCanvas(loaded, targetW, targetH);

  // Iteratively reduce quality
  let q = options.initialQuality;
  let bestBlob = await canvasToBlob(canvas, options.mime, q);

  if (bestBlob.size > options.targetBytes) {
    while (q > options.minQuality && bestBlob.size > options.targetBytes) {
      q = Math.max(
        options.minQuality,
        parseFloat((q - options.qualityStep).toFixed(2)),
      );
      bestBlob = await canvasToBlob(canvas, options.mime, q);
      if (q <= options.minQuality) break;
    }
  }

  return { blob: bestBlob, mime: options.mime, fileName: 'certificate.jpg' };
};
